import React, { useState } from "react";
import { Box, Tabs, Tab } from "@mui/material";
import {
  NotificationProvider,
  PermissionCenterProvider,
  AuthLayout,
  DarkModeProvider,
} from "@1823-partners/core";
import api from "./api";
import Dashboard from "./components/Dashboard";
import PortfolioAnalysis from "./components/PortfolioAnalysis";

/*
 * MUI Chip bug workaround, applied at the theme so it also covers chips this app does not render.
 * Chip's root slot wires up
 *   onClick: event => { handlers.onClick?.(event); onClick(event); }
 * with NO guard on the second call, so clicking a Chip that was given no handler throws
 * "onClick is not a function" and takes the tab down.
 *
 * The chips that hit this are the Granted/Missing labels inside core's permissions drawer, which
 * cannot be patched from here -- the drawer ships compiled inside @1823-partners/core. MUI applies
 * defaultProps to every instance of a component, including ones rendered from another bundle, so
 * supplying the no-op here reaches them.
 *
 * clickable:false is the other half and is not redundant: MUI computes
 *   clickable = clickableProp !== false && onClick ? true : clickableProp
 * so the no-op alone would promote every chip in the app to a ButtonBase with a ripple and a
 * pointer cursor. This app has no genuinely clickable chip; one added later must pass clickable
 * explicitly.
 *
 * Module scope, not inline: DarkModeProvider memoises the theme on this object's identity.
 */
const THEME_OPTIONS = {
  components: {
    MuiChip: { defaultProps: { clickable: false, onClick: () => {} } },
  },
};

export default function App() {
  const [currentTab, setCurrentTab] = useState(0);
  const [prefilledAnalysisData, setPrefilledAnalysisData] = useState(null);

  const handleLoadAnalysis = (configData) => {
    const analysisData = {
      id: configData.ID || null,
      cusips: configData.CUSIPS ? configData.CUSIPS.split(',').map(c => c.trim()) : [],
      asofdt: configData.ASOFDT || '',
      env: configData.ENV || 'UAT',
      scenarios: configData.SCEN_IDS ? configData.SCEN_IDS.split(',').map(s => s.trim()) : [],
      description: configData.DESCRIPTION || '',
    };
    setPrefilledAnalysisData(analysisData);
    setCurrentTab(1);
  };

  return (
    <DarkModeProvider appName="scenarios" themeOptions={THEME_OPTIONS}>
      <NotificationProvider>
        {/* Mounts the permissions drawer (the toolbar's shield button) and, because it wraps
            PermissionRequestProvider itself, keeps turning the "Request access" button on
            permission-denied toasts into an in-app request. Do not also mount
            PermissionRequestProvider -- that nests a second copy. Inside NotificationProvider
            because the request path reports its outcome as a toast. */}
        <PermissionCenterProvider api={api} appName="scenarios" permissionPrefix="scenario">
          <AuthLayout title="Scenarios">
            {({ userInfo }) => (
              <Box>
                <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                  <Tabs value={currentTab} onChange={(_, v) => setCurrentTab(v)}>
                    <Tab label="Dashboard" />
                    <Tab label="Portfolio Analysis" />
                  </Tabs>
                </Box>
                {currentTab === 0 && (
                  <Dashboard
                    userId={userInfo?.userDetails}
                    onLoadAnalysis={handleLoadAnalysis}
                  />
                )}
                {currentTab === 1 && (
                  <PortfolioAnalysis
                    userId={userInfo?.userDetails}
                    prefilledData={prefilledAnalysisData}
                    onDataUsed={() => setPrefilledAnalysisData(null)}
                  />
                )}
              </Box>
            )}
          </AuthLayout>
        </PermissionCenterProvider>
      </NotificationProvider>
    </DarkModeProvider>
  );
}
